import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import LocalizedLink from '@/i18n/LocalizedLink';
import { useLang } from '@/i18n/useLocalizedPath';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FileText,
  Calendar,
  ArrowRight,
  Loader2,
  MapPin,
  Tag,
  User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const [searchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const city = searchParams.get('city');
  const sector = searchParams.get('sector');

  useEffect(() => {
    fetchBlogs();
  }, [city, sector, lang]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (city) params.append('city', city);
      if (sector) params.append('sector', sector);
      params.append('limit', '20');
      params.append('lang', lang);
      
      const response = await axios.get(`${BACKEND_URL}/api/public/blogs?${params}`);
      setBlogs(response.data.blogs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilterTitle = () => {
    if (city && sector) return t('blog.filterCitySector', { sector, city });
    if (city) return t('blog.filterCity', { city });
    if (sector) return t('blog.filterSector', { sector });
    return t('blog.defaultTitle');
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Jigsaw Conferences Events Blog",
    "description": "Expert insights, guides and news about business events, trade shows and conferences across the UK.",
    "url": "https://events.jigsawconferences.co.uk/blog"
  };

  return (
    <MainLayout>
      <SEOHead
        title="Events Blog | Jigsaw Conferences"
        description="Expert insights, guides and news about business events, trade shows and conferences across the UK. Tips for attendees and organisers."
        url="https://events.jigsawconferences.co.uk/blog"
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-orange-500/20 p-3 rounded-full">
              <FileText className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{getFilterTitle()}</h1>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl">
            {t('blog.heroSubtitle')}
          </p>
          
          {/* Filter badges */}
          {(city || sector) && (
            <div className="flex gap-2 mt-6">
              {city && (
                <Badge className="bg-white/10 text-white">
                  <MapPin className="w-3 h-3 mr-1" />
                  {city}
                </Badge>
              )}
              {sector && (
                <Badge className="bg-white/10 text-white">
                  <Tag className="w-3 h-3 mr-1" />
                  {sector}
                </Badge>
              )}
              <LocalizedLink to="/blog">
                <Badge variant="outline" className="text-white border-white/30 hover:bg-white/10">
                  {t('blog.clearFilters')}
                </Badge>
              </LocalizedLink>
            </div>
          )}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">{t('blog.noArticles')}</h2>
              <p className="text-gray-500 mb-4">{t('blog.checkBack')}</p>
              <LocalizedLink to="/events">
                <Button className="bg-orange-600 hover:bg-orange-700">
                  {t('blog.browseEvents')}
                </Button>
              </LocalizedLink>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-6">{t('blog.articlesCount', { count: total })}</p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {blogs.map((blog) => (
                  <LocalizedLink
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    className="block group"
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      {blog.featuredImage && (
                        <div className="aspect-video bg-gray-200 overflow-hidden">
                          <img
                            src={blog.featuredImage}
                            alt={blog.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <div className="flex flex-wrap gap-2 mb-3">
                          {blog.sectorTags?.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        
                        <h2 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-orange-600 line-clamp-2">
                          {blog.title}
                        </h2>
                        
                        <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                          {blog.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            {blog.author}
                          </span>
                          <span className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(blog.publishDate).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div className="flex items-center text-sm font-medium text-orange-600 mt-4 group-hover:text-orange-700">
                          {t('blog.readMore')}
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </LocalizedLink>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('blog.ctaTitle')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('blog.ctaDesc')}
          </p>
          <LocalizedLink to="/events/list-your-event">
            <Button className="bg-orange-600 hover:bg-orange-700">
              {t('blog.ctaButton')}
            </Button>
          </LocalizedLink>
        </div>
      </section>
    </MainLayout>
  );
}
